import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  addDoc,
  doc,
  setDoc,
  collection,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const dedupeContactsByChatId = (contacts = []) => {
  const contactMap = new Map();

  contacts.forEach((contact) => {
    if (!contact?.chatId) return;
    contactMap.set(contact.chatId, {
      ...contactMap.get(contact.chatId),
      ...contact
    });
  });

  return Array.from(contactMap.values());
};

export const createContact = createAsyncThunk(
  'contact/createContact',
  async ({
    myUid,
    myName,
    myPhotoURL,
    contactUid,
    contactName,
    contactPhotoURL,
    contactJobTitle,
    ...jobData
  }) => {
    const userChatDocRef = doc(db, 'users', myUid, 'chats', contactUid);

    // Check if the chat already exists.
    const docSnap = await getDoc(userChatDocRef);
    if (docSnap.exists()) {
      throw new Error('Chat already exists.');
    }

    let finalContactPhotoURL = contactPhotoURL;
    if (!finalContactPhotoURL || !finalContactPhotoURL.startsWith('http')) {
      const userDataSnap = await getDoc(doc(db, 'users', contactUid, 'data', 'userdata'));
      if (userDataSnap.exists() && userDataSnap.data()?.photoURL) {
        finalContactPhotoURL = userDataSnap.data().photoURL;
      }
    }

    let finalMyPhotoURL = myPhotoURL;
    if (!finalMyPhotoURL || !finalMyPhotoURL.startsWith('http')) {
      const myUserDataSnap = await getDoc(doc(db, 'users', myUid, 'data', 'userdata'));
      if (myUserDataSnap.exists() && myUserDataSnap.data()?.photoURL) {
        finalMyPhotoURL = myUserDataSnap.data().photoURL;
      }
    }

    // If chat does not exist, create a new chat structure with members.
    const chatData = {
      memberUids: [myUid, contactUid],
      members: [
        {
          uid: myUid,
          name: myName,
          photoURL: finalMyPhotoURL
        },
        {
          uid: contactUid,
          name: contactName,
          photoURL: finalContactPhotoURL,
          contactJobTitle: contactJobTitle,
          ...jobData
        }
      ],
      lastMessage: null,
      unreadCounts: { [myUid]: 0, [contactUid]: 0 }
    };
    // If chat does not exist, create a new chat log.
    const chatRef = await addDoc(collection(db, 'chatlogs'), chatData);

    // Add chat reference to the current user's list of chats.
    await setDoc(userChatDocRef, {
      contactUid: contactUid,
      chatId: chatRef.id,
      name: contactName,
      photoURL: finalContactPhotoURL,
      contactJobTitle: contactJobTitle,
      job: { ...jobData }
    });

    // Add chat reference to the contact's list of chats.
    await setDoc(doc(db, 'users', contactUid, 'chats', myUid), {
      contactUid: myUid,
      chatId: chatRef.id,
      name: myName,
      photoURL: finalMyPhotoURL,
      contactJobTitle: contactJobTitle,
      job: { ...jobData }
    });

    // Return the new chat's details.
    return {
      contactUid: contactUid,
      chatId: chatRef.id,
      name: contactName,
      photoURL: finalContactPhotoURL,
      contactJobTitle: contactJobTitle,
      job: { ...jobData }
    };
  }
);

export const fetchChatlogs = createAsyncThunk('chat/fetchChatlogs', async () => {
  const chatlogsCollection = collection(db, 'chatlogs');
  const snapshot = await getDocs(chatlogsCollection);
  const chatlogs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  return chatlogs;
});

export const fetchContacts = createAsyncThunk('contact/fetchContacts', async (uid) => {
  const documents = [];
  const snap = await getDocs(collection(db, 'users', uid, 'chats'));
  for (let doc of snap.docs) {
    documents.push(doc.data());
  }
  return documents;
});

export const deleteContact = createAsyncThunk(
  'contact/deleteContact',
  async ({ userUid, contactUid, chatId }) => {
    await deleteDoc(doc(db, 'users', userUid, 'chats', contactUid));
    if (chatId) {
      await updateDoc(doc(db, 'chatlogs', chatId), {
        [`hiddenBefore.${userUid}`]: serverTimestamp(),
      [`unreadCounts.${userUid}`]: 0
      });
    }
    return contactUid;
  }
);

const initialState = {
  contacts: [],
  error: null // Added an error state
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState: {
    chatlogs: [],
    status: 'idle',
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatlogs.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchChatlogs.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.chatlogs = action.payload;
      })
      .addCase(fetchChatlogs.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  }
});

export const contactSlice = createSlice({
  name: 'contact',
  initialState: initialState,
  reducers: {
    resetContact() {
      return initialState;
    },
    addContact(state, action) {
      if (!Array.isArray(state.contacts)) state.contacts = [];
      state.contacts = dedupeContactsByChatId([...state.contacts, action.payload]);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createContact.fulfilled, (state, action) => {
        state.contacts = dedupeContactsByChatId([...state.contacts, action.payload]);
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.contacts = dedupeContactsByChatId(action.payload);
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.contacts = state.contacts.filter((c) => c.contactUid !== action.payload);
      })
      // Added error handling for all thunks
      .addCase(createContact.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.error = action.error.message;
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.error = action.error.message;
      });
  }
});

export const { resetContact, addContact } = contactSlice.actions;
export default contactSlice.reducer;


